import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  ClientResponse,
  ErrorWithMessage,
  ReduxStoreRoot,
  LocationDataFields,
  LocationsResponse,
} from "../../types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { arraysHaveSameOrder, fetchFunction } from "../../util";
import {
  Box,
  Center,
  Loading,
  Title,
  Text,
  hooks,
  Cluster,
  ToastNotification,
} from "@thecb/components";
import Location from "../../types/api/models/location";
import { TableWrapper } from "../../components/styled";
import {
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
  TableRowEntry,
} from "../../components/table";
import { useHistory } from "react-router-dom";
import { RequestMethod } from "../../types/api/RequestMethod";
import { AlertVariants } from "../../types/Alert";
import DragHandle from "../../components/drag-handle";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";

const tableColumns = ["Location", "Address"];

const LocationsTable = () => {
  const history = useHistory();
  const { isToastOpen, toastVariant, toastMessage, showToast, hideToast } =
    hooks.useToastNotification({ timeout: 3000 });
  const { posApiEndpoint } = useSelector((state: ReduxStoreRoot) => state.config);

  // locationsCache is used to revert locations to its previous state if the drag and drop sort request errors
  const [locations, setLocations] = useState<LocationDataFields[]>([]);
  const [locationsCache, setLocationsCache] = useState<LocationDataFields[]>([]);
  const locationIds = locations.map((location) => location.id);

  const {
    client: {
      value: { brand: selectedBrand },
    },
  } = useSelector((state: ReduxStoreRoot) => state.currentUser);

  const { data: clientsGETResponse, isFetching: isFetchingClient } = useQuery<
    ClientResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /clients/${selectedBrand}`],
    queryFn: () => fetchFunction(`${posApiEndpoint}/clients/${selectedBrand}`),
  });
  const { isFetching: isFetchingLocations } = useQuery<
    LocationsResponse,
    ErrorWithMessage
  >({
    queryKey: [`GET -> /locations?brand=${clientsGETResponse?.data.brand}`],
    queryFn: () =>
      fetchFunction(`${posApiEndpoint}/locations?brand=${selectedBrand}`),
    onSuccess: (res) => {
      setLocations(res.data);
      setLocationsCache(res.data);
    },
  });
  const { mutate: saveLocationSortOrders } = useMutation<
    LocationsResponse,
    ErrorWithMessage,
    { location_ids: number[] }
  >({
    mutationFn: (body: { location_ids: number[] }) =>
      fetchFunction(`${posApiEndpoint}/locations/sort`, {
        method: RequestMethod.PUT,
        body,
      }),
    onSuccess: () => {
      showToast({
        message: "Sort Order Updated",
        variant: AlertVariants.SUCCESS,
      });
    },
    onError: () => {
      setLocations(locationsCache);
      showToast({
        message: "Error Updating Sort Order",
        variant: AlertVariants.ERROR,
      });
    },
  });

  const onRowClick = (location: Location) =>
    history.push(`${history.location.pathname}/${location.id}/edit`);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    const oldIndex = locations.findIndex((location) => location.id === active.id);
    const newIndex = locations.findIndex((location) => location.id === over?.id);

    const newLocationsList = arrayMove(locations, oldIndex, newIndex);
    const newLocationIds = newLocationsList.map((location: Location) => location.id);

    if (!arraysHaveSameOrder(locationIds, newLocationIds)) {
      setLocations(newLocationsList);
      saveLocationSortOrders({ location_ids: newLocationIds });
    }
  };

  interface Props {
    location: Location;
  }
  const SortableRow = ({ location }: Props) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
    } = useSortable({ id: location.id });
    const style = {
      transition,
      transform: CSS.Transform.toString(transform),
    };
    return (
      <TableRow
        onClick={() => onRowClick(location)}
        extraStyles={`
          transition: ${style.transition}; 
          transform: ${style.transform}
        `}
        setNodeRef={setNodeRef}
      >
        <TableRowEntry>{location.name}</TableRowEntry>
        <TableRowEntry>
          <Cluster justify="space-between">
            {location.address_1}
            <DragHandle {...attributes} {...listeners} ref={setActivatorNodeRef} />
          </Cluster>
        </TableRowEntry>
      </TableRow>
    );
  };

  if (isFetchingClient || isFetchingLocations) {
    return <Loading />;
  }

  return (
    <TableWrapper>
      <ToastNotification
        variant={toastVariant}
        message={toastMessage}
        toastOpen={isToastOpen}
        closeToastNotification={() => hideToast()}
      />
      <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <Table>
          <TableHeader>
            {tableColumns.map((c, idx) => (
              <TableHeading extraStyles={`text-align: left !important;`} key={idx}>
                {c}
              </TableHeading>
            ))}
          </TableHeader>
          <SortableContext
            items={locationIds}
            strategy={verticalListSortingStrategy}
          >
            <TableBody>
              {locations.map((location) => (
                <SortableRow key={location.id} location={location} />
              ))}
            </TableBody>
          </SortableContext>
        </Table>
      </DndContext>
      {!locations.length && (
        <Box padding="64px" minWidth="100%">
          <Center intrinsic>
            <Title as="h2" weight="700" margin="0 0 0.25rem">
              No Locations
            </Title>
            <Text>
              This client has no locations. Create a location to see them listed
              here.
            </Text>
          </Center>
        </Box>
      )}
    </TableWrapper>
  );
};

export default LocationsTable;
